import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, media } from "@/lib/api";
import { motion } from "framer-motion";
import {
  ArrowRight, Play, Leaf, Brain, Infinity as InfinityIcon, Flame,
  ChevronRight, ChevronLeft, ChevronDown, FlaskConical,
} from "lucide-react";
import { Icon } from "@/lib/icons";
import { DEFAULT_HOME as _D, mergeHome } from "@/lib/homeContent";
void _D;
import { ParallaxScene } from "@/components/fx/ParallaxScene";
import { CustomCursor } from "@/components/fx/CustomCursor";
import { Ambient } from "@/components/fx/Ambient";
import { ScrollRail } from "@/components/fx/ScrollRail";
import { TiltCard } from "@/components/fx/TiltCard";
import { Fade, WordReveal } from "@/components/fx/Reveal";

const SECTIONS = [
  { id: "hero", label: "Enter" },
  { id: "method", label: "Cases" },
  { id: "pathway", label: "Pathway" },
  { id: "protocols", label: "Protocols" },
  { id: "science", label: "Science" },
  { id: "tools", label: "Instruments" },
];

const BESTSELLERS = ["tirzepatide", "retatrutide", "bpc-tb-blend", "semaglutide", "nad-plus", "ghk-cu"];

const SideWords = ({ words = [], className = "" }) => (
  <div className={`hidden xl:block absolute z-10 side-words ${className}`} aria-hidden="true">
    {words.map((w, i) => (
      <motion.div key={w + i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }} transition={{ delay: 0.5 + i * 0.12, duration: 0.8 }}>{w}</motion.div>
    ))}
    <div className="w-8 h-px bg-white/40 mt-3" />
  </div>
);

const PillLink = ({ to, children, testid, ghost }) => (
  <Link to={to || "#"} data-testid={testid} data-cursor="ENTER"
    className={`inline-flex items-center gap-3 px-7 py-3.5 rounded-full text-[12px] ${ghost ? "btn-ghost" : "btn-primary"}`}>
    {children} <ArrowRight size={14} />
  </Link>
);

const CAT_META = {
  recov: [Leaf, "RECOVERY"], cogni: [Brain, "COGNITION"], longe: [InfinityIcon, "LONGEVITY"],
  metab: [Flame, "METABOLIC"], perfo: [Flame, "PERFORMANCE"], cellu: [InfinityIcon, "CELLULAR"], skin: [Leaf, "SKIN"],
};

const ProductCard = ({ p, i, delay, category }) => {
  const idxTag = ["RECOVERY", "LONGEVITY", "COGNITION", "METABOLIC"][i % 4];
  const idxIcon = [Leaf, InfinityIcon, Brain, Flame][i % 4];
  const idxSub = [
    "Accelerate healing. Restore performance.",
    "Extend healthspan. Age without limits.",
    "Sharpen focus. A clearer tomorrow.",
    "Activate balance. Fuel potential.",
  ][i % 4];
  const meta = category ? CAT_META[String(category).toLowerCase().slice(0, 5)] : null;
  const tag = meta ? meta[1] : idxTag;
  const TagIcon = meta ? meta[0] : idxIcon;
  const sub = category ? (p.tagline || idxSub) : idxSub;
  const art = ["/img/gen-vial.jpg", "/img/gen-body.jpg", "/img/gen-brain.jpg", "/img/gen-atom.jpg"][i % 4];
  const price = p.variants?.[0]?.price;
  return (
    <TiltCard d={delay} testid={`peptide-tilt-${p.slug}`} className="glass rounded-[24px] p-5 w-full sm:w-[212px]">
      <Link to={`/shop/${p.slug}`} data-testid={`peptide-card-${p.slug}`} data-cursor="VIEW" className="block relative z-10">
        <div className="flex items-center justify-between card-num"><span>CG-{101 + i * 101}</span><span className="w-4 h-px bg-white/40" /></div>
        <div className="mt-3 mx-auto w-[118px] h-[136px] rounded-2xl overflow-hidden relative shadow-[0_0_36px_rgba(142,197,255,0.32)]">
          <img src={media(p.image) || art} alt={p.name} loading="lazy" className="w-full h-full object-cover scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#04060b]/85 via-transparent to-transparent" />
          <div className="scan-line" />
          <div className="absolute inset-x-0 bottom-2.5 text-center">
            <div className="text-[8px] font-mono tracking-wider text-white/95 leading-tight px-1 drop-shadow">{p.name.replace("CG Labz ", "")}</div>
          </div>
        </div>
        <TagIcon size={18} className="mt-4 text-[#DCEBFF]" />
        <div className="mt-1 serif-display text-lg text-white">{tag}</div>
        <p className="mt-1 text-[12px] text-slate-300/85 leading-snug min-h-[34px] line-clamp-3">{sub}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-ghost text-[10px]">View <ArrowRight size={11} /></span>
          {price && <span className="text-[11px] font-mono text-[#DCEBFF]/85">${price}</span>}
        </div>
      </Link>
    </TiltCard>
  );
};

const BestsellerCard = ({ p, i }) => (
  <TiltCard d={0.5 + i * 0.1} testid={`bestseller-tilt-${p.slug}`}
    className="group w-full glass rounded-2xl overflow-hidden border border-white/10 hover:border-amber-300/40 transition-colors duration-500">
    <Link to={`/shop/${p.slug}`} data-testid={`bestseller-card-${p.slug}`} data-cursor="VIEW" className="block relative z-10">
      <div className="relative h-24 sm:h-28 overflow-hidden bg-gradient-to-b from-slate-900/40 to-black/60 flex items-center justify-center">
        {p.image
          ? <img src={media(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]" />
          : <FlaskConical size={24} className="text-slate-600" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#04060b] via-transparent to-transparent" />
        <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-amber-400/15 border border-amber-300/40 text-[8px] font-mono uppercase tracking-widest text-amber-200">#{i + 1}</span>
      </div>
      <div className="p-3">
        <div className="serif-display text-[13px] leading-tight text-white line-clamp-2 min-h-[32px]">{p.name.replace("CG Labz ", "")}</div>
        <div className="mt-2 flex items-center justify-between gap-1">
          <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-slate-300 group-hover:text-amber-200 transition-colors">View <ArrowRight size={10} /></span>
          {p.from_price != null && (
            <span className="text-[11px] font-mono text-amber-200/90"><span className="text-[8px] text-slate-500 mr-0.5">from</span>${p.from_price.toFixed(2)}</span>
          )}
        </div>
      </div>
    </Link>
  </TiltCard>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  const [c, setC] = useState(mergeHome({}));
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/content/home").then(({ data }) => setC(mergeHome(data))).catch(() => {});
  }, []);

  const bestsellers = BESTSELLERS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  const caseProducts = products.filter((p) => p.product_type === "case");
  const casesRef = useRef(null);
  const scrollCases = (dir) => casesRef.current?.scrollBy({ left: dir * 364, behavior: "smooth" });
  const { hero, method, cases, pathway, protocols, science, tools } = c;

  const vialProducts = products.filter((p) => p.product_type !== "case");
  const activeCat = pathway.pathways?.[active]?.category;
  const catKey = String(activeCat || "").toLowerCase().slice(0, 5);
  const matchedProducts = activeCat
    ? vialProducts.filter((p) => (p.categories || []).some((cc) => String(cc).toLowerCase().slice(0, 5) === catKey))
    : vialProducts;
  const pathwayProducts = (matchedProducts.length ? matchedProducts : vialProducts).slice(0, 3);

  return (
    <div className="bg-[#04060b] overflow-x-hidden" data-testid="home-immersive">
      <CustomCursor />
      <Ambient />
      <ScrollRail sections={SECTIONS} />

      {/* 1 — HERO */}
      <ParallaxScene id="hero" bg={media(hero.bg_image)} tint="tint-cyan" className="min-h-screen pt-[76px]">
        <SideWords words={hero.sidewords_left} className="left-10 bottom-10" />
        <SideWords words={hero.sidewords_right} className="right-10 top-32 text-right [&>div:last-child]:ml-auto" />
        <div className="px-4 sm:px-8 lg:px-12 w-full py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 xl:pl-6 min-w-0">
            <Fade className="side-words mb-6 flex items-center gap-4 flex-wrap">
              <span className="pulse-dot inline-block" />
              {(hero.badges || []).map((b, i) => (
                <span key={b + i} className="flex items-center gap-4">{i > 0 && <span className="text-white/40">/</span>}{b}</span>
              ))}
            </Fade>
            <WordReveal as="h1" text={hero.title} className="hero-title text-[30px] sm:text-5xl lg:text-6xl xl:text-[65px] text-white" />
            <Fade d={0.55} className="mt-7 text-slate-200/90 text-base sm:text-lg max-w-md leading-relaxed whitespace-pre-line">
              {hero.subtitle}
            </Fade>
            <Fade d={0.68} className="mt-9 flex flex-wrap items-center gap-6">
              <PillLink to={hero.primary_link} testid="hero-explore-btn">{hero.primary_label}</PillLink>
              <Link to={hero.secondary_link || "/journal"} data-testid="hero-story-btn" data-cursor="PLAY"
                className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-200 hover:text-white group">
                <span className="icon-ring !w-11 !h-11"><Play size={14} className="ml-0.5" fill="currentColor" /></span> {hero.secondary_label}
              </Link>
            </Fade>
          </div>
          <div className="lg:col-span-6 w-full min-w-0" data-testid="home-bestsellers">
            <div className="flex items-center gap-2 mb-4 px-1">
              <Flame size={14} className="text-amber-300" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-amber-200">Bestsellers right now</span>
            </div>
            <div className="grid grid-cols-3 gap-2.5 sm:gap-3" data-testid="home-bestsellers-grid">
              {bestsellers.slice(0, 3).map((p, i) => <BestsellerCard key={p.id} p={p} i={i} />)}
            </div>
          </div>
        </div>
        <motion.button
          onClick={() => document.getElementById("method")?.scrollIntoView({ behavior: "smooth" })}
          data-testid="enter-experience-btn" data-cursor="SCROLL"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          className="absolute z-20 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
          <span className="cg-enter-ring"><ChevronDown size={16} /><span className="cg-hud !text-[7px] mt-1">Enter</span></span>
          <span className="cg-hud mt-3">Enter Experience</span>
        </motion.button>
      </ParallaxScene>

      {/* 2 — CASES SHOWCASE */}
      <ParallaxScene id="method" bg={media(cases.bg_image)} className="py-28 lg:py-36">
        <div className="px-4 sm:px-8 lg:px-12 w-full max-w-6xl mx-auto">
          {/* header */}
          <div className="text-center max-w-2xl mx-auto">
            <Fade className="side-words mb-5 justify-center flex items-center gap-3">{cases.eyebrow}</Fade>
            <WordReveal text={cases.title} className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white" />
            <Fade d={0.4} className="mt-6 text-slate-200/85 text-base sm:text-lg leading-relaxed">
              {cases.intro}
            </Fade>
            <Fade d={0.5} className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3">
              {(cases.badges || []).map((b) => (
                <span key={b.label} className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-slate-300/80">
                  <Icon name={b.icon} size={14} className="text-cyan-300" />{b.label}
                </span>
              ))}
            </Fade>
          </div>

          {/* cards — one-row carousel */}
          <div className="relative mt-16" data-testid="home-cases-carousel">
            {caseProducts.length > 1 && (
              <>
                <button onClick={() => scrollCases(-1)} aria-label="Previous cases" data-testid="cases-prev"
                  className="hidden sm:flex absolute left-0 top-[130px] -translate-x-1/2 z-20 w-11 h-11 rounded-full glass border border-white/15 items-center justify-center text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
                  <ChevronLeft size={18} />
                </button>
                <button onClick={() => scrollCases(1)} aria-label="Next cases" data-testid="cases-next"
                  className="hidden sm:flex absolute right-0 top-[130px] translate-x-1/2 z-20 w-11 h-11 rounded-full glass border border-white/15 items-center justify-center text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
                  <ChevronRight size={18} />
                </button>
              </>
            )}
            <div ref={casesRef} data-testid="home-cases-grid"
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-3 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {caseProducts.map((p, i) => (
                <TiltCard key={p.id} d={0.2 + i * 0.1} testid={`case-tilt-${i}`} className="group shrink-0 w-[300px] sm:w-[340px] snap-start rounded-[26px] bg-white/[0.03] border border-white/10 hover:border-cyan-400/40 transition-colors duration-500">
                  <Link to={`/shop/${p.slug}`} data-testid={`home-case-card-${p.slug}`} data-cursor="OPEN" className="flex flex-col h-full relative z-10 rounded-[26px] overflow-hidden">
                    <div className="relative h-56 overflow-hidden bg-gradient-to-b from-slate-900/40 to-black/60">
                      {p.image && <img src={media(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#04060b] via-transparent to-transparent" />
                      <span className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur border border-violet-400/40 text-[10px] font-mono uppercase tracking-widest text-violet-200">Case</span>
                      {p.vial_capacity != null && (
                        <span className="absolute bottom-4 left-5 inline-flex items-center gap-1.5 text-white">
                          <span className="text-2xl font-mono leading-none">{p.vial_capacity}</span>
                          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-300/80 leading-tight">vial<br/>slots</span>
                        </span>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="serif-display text-xl text-white leading-tight">{p.name.replace("CG Labz ", "")}</div>
                      <p className="mt-2 text-[13px] text-slate-300/75 leading-relaxed line-clamp-2 min-h-[38px]">{p.tagline}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {p.bac_water_slots ? <span className="px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-[11px] font-mono text-cyan-300">+ bac water spot</span> : null}
                        {p.fits_vials && <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-mono text-slate-300">{p.fits_vials.split("(")[0].trim()}</span>}
                      </div>
                      <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          {p.price != null && <span className="text-2xl font-mono text-white">${p.price.toFixed(2)}</span>}
                          {p.compare_at_price > p.price && <span className="text-xs font-mono text-slate-500 line-through">${p.compare_at_price.toFixed(2)}</span>}
                        </div>
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-[11px] font-mono uppercase tracking-widest text-slate-200 group-hover:border-cyan-400/50 group-hover:text-cyan-300 group-hover:gap-3 transition-all duration-300">
                          View <ArrowRight size={13} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </TiltCard>
              ))}
            </div>
          </div>

          <Fade d={0.3} className="mt-14 text-center">
            <PillLink to={cases.cta_link} testid="cases-shop-btn">{cases.cta_label}</PillLink>
          </Fade>
        </div>
      </ParallaxScene>

      {/* 3 — PATHWAY */}
      <ParallaxScene id="pathway" bg={media(pathway.bg_image)} tint="tint-cyan" className="py-28">
        <div className="px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center w-full">
          <Fade className="lg:col-span-4 glass rounded-[28px] p-6 sm:p-8" data-testid="pathway-panel">
            <div className="serif-display text-3xl text-white text-center">{pathway.panel_title}</div>
            <div className="side-words text-center mt-2 !text-[9px]">Personalized science. A higher you.</div>
            <div className="mt-6 space-y-2">
              {(pathway.pathways || []).map((pw, i) => (
                <div key={i} role="button" tabIndex={0} data-cursor="SELECT"
                  onMouseEnter={() => setActive(i)} onClick={() => setActive(i)} data-testid={`pathway-${pw.category}`}
                  className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all duration-500 ${active === i ? "border-[#8EC5FF]/80 bg-[#8EC5FF]/10 shadow-[0_0_28px_rgba(142,197,255,0.28)]" : "border-transparent hover:border-white/15"}`}>
                  <span className="icon-ring shrink-0"><Icon name={pw.icon} size={20} /></span>
                  <span className="flex-1">
                    <span className="serif-display text-lg text-white block">{pw.title}</span>
                    <span className="text-[12px] text-slate-300/85 leading-snug block mt-0.5">{pw.desc}</span>
                  </span>
                  <Link to={`/peptides?category=${pw.category}`} className="arrow-ring !w-8 !h-8 shrink-0"><ChevronRight size={13} /></Link>
                </div>
              ))}
            </div>
            <div className="side-words text-center mt-5 !text-[9px]">Different paths. A higher you.</div>
          </Fade>
          <div className="lg:col-span-8 lg:pl-[20%]">
            <div className="text-center mb-8">
              <Fade className="side-words">Curated for you</Fade>
              <WordReveal text={pathway.title} className="serif-display text-4xl sm:text-5xl text-white mt-2" />
              <Fade d={0.4} className="side-words mt-2">{pathway.subtitle}</Fade>
            </div>
            <div className="flex flex-wrap justify-center gap-4" data-testid="pathway-products">
              {pathwayProducts.map((p, i) => <ProductCard key={`${p.id}-${active}`} p={p} i={i} delay={0.25 + i * 0.12} category={activeCat} />)}
            </div>
            <Fade d={0.6} className="text-center mt-10">
              <Link to="/bulk" data-testid="build-stack-btn" data-cursor="BUILD"
                className="inline-flex items-center gap-3 px-12 py-4 rounded-full btn-primary text-[13px]">Build Your Stack <ArrowRight size={15} /></Link>
              <div className="side-words mt-4">Personalized protocols. Extraordinary outcomes.</div>
            </Fade>
          </div>
        </div>
      </ParallaxScene>

      {/* 4 — PROTOCOLS */}
      <ParallaxScene id="protocols" bg={media(protocols.bg_image)} tint="tint-rose" className="py-28">
        <SideWords words={["Science", "Longevity", "Performance", "A Higher You"]} className="left-12 top-24" />
        <SideWords words={["Precision", "Peptides", "Real Results", "Tomorrow", "Today"]} className="right-12 top-24 text-right [&>div:last-child]:ml-auto" />
        <div className="px-4 sm:px-8 lg:px-12 w-full">
          <div className="text-center">
            <Fade className="side-words">{protocols.eyebrow}</Fade>
            <WordReveal text={protocols.title} className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mt-3" />
            <Fade d={0.45} className="mt-4 text-slate-200/90 text-base sm:text-lg">{protocols.intro}</Fade>
          </div>
          <div className="relative mt-14 max-w-6xl mx-auto" data-testid="home-protocols-carousel">
            <div data-testid="home-protocols-track"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 justify-items-center">
                {(protocols.items || []).map((it, i) => (
                  <TiltCard key={i} d={0.15 + i * 0.09} testid={`protocol-card-${i}`}
                    className="glass rounded-[26px] p-6 w-full max-w-[320px] flex flex-col min-h-[390px]">
                    <div className="relative z-10 flex flex-col h-full">
                      <div className="flex items-center justify-between"><span className="card-num">0{i + 1} <span className="inline-block w-5 h-px bg-white/40 align-middle ml-2" /></span><Icon name={it.icon} size={18} className="text-[#DCEBFF]" /></div>
                      <div className="serif-display text-xl text-white mt-5 leading-tight break-words">{it.title}</div>
                      <div className="my-5 h-[112px] rounded-2xl overflow-hidden relative">
                        <img src={media(it.image)} alt="" loading="lazy" className="w-full h-full object-cover opacity-90 transition-transform duration-700 hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#04060b]/70 to-transparent" />
                      </div>
                      <p className="text-[13px] text-slate-200/85 leading-relaxed">{it.desc}</p>
                      <div className="mt-auto pt-5">
                        <Link to={`/peptides?category=${it.category}`} data-cursor="OPEN" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-ghost text-[10px]">Explore <ArrowRight size={11} /></Link>
                      </div>
                    </div>
                  </TiltCard>
                ))}
            </div>
          </div>
        </div>
      </ParallaxScene>

      {/* 5 — SCIENCE */}
      <ParallaxScene id="science" bg={media(science.bg_image)} className="py-28 lg:py-36">
        <SideWords words={["Precision", "Peptides", "Real Results"]} className="left-12 bottom-24" />
        <SideWords words={["Human", "Potential", "Reimagined"]} className="right-12 bottom-24 text-right [&>div:last-child]:ml-auto" />
        <div className="px-4 sm:px-8 lg:px-12 w-full">
          <div className="text-center">
            <WordReveal text={science.title} className="hero-title text-4xl sm:text-6xl lg:text-7xl text-white" />
            <Fade d={0.5} className="side-words mt-5 !text-[11px] !tracking-[0.35em]">{science.subtitle}</Fade>
          </div>
          <div className="mt-16 lg:mt-24 grid lg:grid-cols-2 gap-6 lg:gap-x-[24vw] lg:gap-y-16 max-w-7xl mx-auto">
            {(science.items || []).map((it, i) => (
              <TiltCard key={i} d={0.15 + i * 0.12} testid={`science-card-${i}`} className="glass rounded-[24px]">
                <Link to={it.link || "#"} data-testid={`science-link-${i}`} data-cursor="VIEW" className="p-6 flex gap-5 items-start group relative z-10">
                  <span className="icon-ring shrink-0"><Icon name={it.icon} size={22} /></span>
                  <span className="flex-1">
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-[15px] font-semibold tracking-[0.18em] uppercase text-white">{it.title}</span>
                      <span className="arrow-ring !w-8 !h-8 shrink-0"><ArrowRight size={13} /></span>
                    </span>
                    <span className="block text-[13px] text-slate-200/85 mt-3 leading-relaxed max-w-[240px]">{it.desc}</span>
                  </span>
                </Link>
              </TiltCard>
            ))}
          </div>
          <Fade d={0.4} className="text-center mt-20 side-words">— Science lives longer —</Fade>
        </div>
      </ParallaxScene>

      {/* 6 — INSTRUMENTS */}
      <section id="tools" className="relative px-4 sm:px-8 lg:px-12 py-28 max-w-7xl mx-auto" data-testid="scene-tools">
        <Fade className="side-words mb-3">{tools.eyebrow}</Fade>
        <WordReveal text={tools.title} className="serif-display text-3xl sm:text-4xl text-white mb-10" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(tools.items || []).map((it, i) => (
            <TiltCard key={i} d={0.1 + i * 0.1} testid={`tool-tilt-${i}`} className="glass rounded-[24px]">
              <Link to={it.link || "#"} data-testid={`tool-link-${i}`} data-cursor="OPEN" className="p-6 group block relative z-10">
                <div className="text-base font-medium text-white">{it.title}</div>
                <div className="text-[13px] text-slate-300/80 mt-1">{it.desc}</div>
                <div className="mt-5 arrow-ring !w-9 !h-9"><ArrowRight size={13} /></div>
              </Link>
            </TiltCard>
          ))}
        </div>
      </section>
    </div>
  );
}
